import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Check, Flag, Loader2, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AGENT_TURN_DONE_EVENT } from '../lib/agents';
import { formatFigure } from '../lib/model';
import type { Milestone, Plan, PlanMetric, PlanStatus, Task } from '../lib/types';
import { formatDueDate } from './TaskCard';
import { InlineError } from './InlineError';

export const PLAN_STATUSES: { id: PlanStatus; label: string }[] = [
    { id: 'draft', label: 'Draft' },
    { id: 'active', label: 'Active' },
    { id: 'done', label: 'Done' },
    { id: 'dropped', label: 'Dropped' },
];

const STATE_LABELS: Record<Milestone['state'], string> = {
    planned: 'Planned',
    in_progress: 'In progress',
    achieved: 'Achieved',
    missed: 'Missed',
    dropped: 'Dropped',
    overdue: 'Overdue',
};

const STATE_TONES: Record<Milestone['state'], string> = {
    planned: 'bg-offwhite text-coolslate',
    in_progress: 'bg-brand/10 text-brand-dark',
    achieved: 'bg-sage/20 text-graphite',
    missed: 'bg-amber-100 text-amber-800',
    dropped: 'bg-offwhite text-coolslate',
    overdue: 'bg-amber-100 text-amber-800',
};

// The business's plans, with each milestone measured on the Model. Reloaded after
// each agent turn, since the agent sets up most of them.
export function usePlans(status?: PlanStatus) {
    const { authFetch } = useAuth();
    const [plans, setPlans] = useState<Plan[] | null>(null);

    const load = useCallback(() => {
        authFetch(`/plans${status ? `?status=${status}` : ''}`)
            .then(r => r.ok ? r.json() : [])
            .then(setPlans)
            .catch(() => setPlans([]));
    }, [authFetch, status]);

    useEffect(load, [load]);
    useEffect(() => {
        window.addEventListener(AGENT_TURN_DONE_EVENT, load);
        return () => window.removeEventListener(AGENT_TURN_DONE_EVENT, load);
    }, [load]);

    return { plans, reload: load };
}

// The lines of the Model a milestone can be measured on, and the company's currency.
export function usePlanMetrics() {
    const { authFetch } = useAuth();
    const [metrics, setMetrics] = useState<{ metrics: PlanMetric[]; currency: string } | null>(null);
    useEffect(() => {
        authFetch('/plans/metrics')
            .then(r => r.ok ? r.json() : { metrics: [], currency: '' })
            .then(setMetrics)
            .catch(() => setMetrics({ metrics: [], currency: '' }));
    }, [authFetch]);
    return metrics;
}

export function usePlanWrite(onChanged: () => void) {
    const { authFetch } = useAuth();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (path: string, method: string, body?: unknown) => {
        setBusy(true);
        setError(null);
        try {
            const res = await authFetch(path, {
                method,
                ...(body === undefined ? {} : {
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            });
            if (!res.ok) {
                const detail = (await res.json().catch(() => ({}))).detail;
                throw new Error(typeof detail === 'string' ? detail : 'Could not save that change');
            }
            onChanged();
            return true;
        } catch (e: any) {
            setError(e.message);
            return false;
        } finally {
            setBusy(false);
        }
    };

    return { run, busy, error };
}

function metricCell(m: Milestone, amount: number) {
    return { value: { amount, unit: m.unit ?? '' } } as Parameters<typeof formatFigure>[0];
}

// Where a measured milestone stands, as a share of its target in the line's direction.
export function milestoneShare(m: Milestone, better: PlanMetric['better'] | undefined): number {
    const current = m.progress?.current;
    if (current === null || current === undefined || !m.target) return 0;
    if (m.progress?.met) return 1;
    return better === 'lower' ? Math.min(1, m.target / current) : Math.max(0, Math.min(1, current / m.target));
}

export function MilestoneMeasure({ milestone: m, metric }: { milestone: Milestone; metric?: PlanMetric }) {
    if (!m.concept || m.target === null) return null;
    const unit = metric?.unit ?? 'count';
    const current = m.progress?.current;
    const tone = m.state === 'achieved' ? 'bg-sage' : m.state === 'overdue' ? 'bg-amber-400' : 'bg-brand';
    return (
        <div className="mt-1.5">
            <div className="h-1.5 rounded-full bg-softgrey/60 overflow-hidden">
                <i className={`block h-full ${tone}`} style={{ width: `${Math.round(milestoneShare(m, metric?.better) * 100)}%` }} />
            </div>
            <p className="text-[11px] text-coolslate mt-1">
                {metric?.name ?? m.concept}{' '}
                {current === null || current === undefined
                    ? <>to reach {formatFigure(metricCell(m, m.target), unit)}, no figure on the Model yet</>
                    : <>{formatFigure(metricCell(m, current), unit)} of {formatFigure(metricCell(m, m.target), unit)}
                        {m.progress?.as_at && <> at {formatDueDate(m.progress.as_at)}</>}</>}
            </p>
        </div>
    );
}

export function MilestoneState({ state }: { state: Milestone['state'] }) {
    return (
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${STATE_TONES[state]}`}>
            {STATE_LABELS[state]}
        </span>
    );
}

function MilestoneRow({ milestone: m, metric, tasks, onChanged }: {
    milestone: Milestone;
    metric?: PlanMetric;
    tasks: Task[];
    onChanged: () => void;
}) {
    const { run, busy, error } = usePlanWrite(onChanged);
    const achieved = m.status === 'achieved';
    const mine = tasks.filter(t => t.milestone_id === m.id);

    return (
        <li className="py-3">
            <div className="flex items-start gap-3">
                {!m.concept ? (
                    <button
                        onClick={() => run(`/plans/milestones/${m.id}`, 'PATCH', { status: achieved ? 'in_progress' : 'achieved' })}
                        disabled={busy}
                        aria-label={achieved ? `Mark ${m.name} not achieved` : `Mark ${m.name} achieved`}
                        aria-pressed={achieved}
                        className={`mt-0.5 w-4 h-4 rounded border grid place-items-center shrink-0 ${achieved ? 'bg-sage border-sage text-white' : 'border-softgrey bg-white'}`}
                    >
                        {achieved && <Check className="w-3 h-3" />}
                    </button>
                ) : (
                    <Flag className="mt-0.5 w-4 h-4 text-brand shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className={`text-sm font-medium ${m.status === 'dropped' ? 'text-coolslate line-through' : 'text-graphite'}`}>{m.name}</p>
                        <MilestoneState state={m.state} />
                    </div>
                    {m.description && <p className="text-[13px] text-coolslate mt-0.5">{m.description}</p>}
                    <MilestoneMeasure milestone={m} metric={metric} />
                    <p className="mt-1 text-[11px] text-coolslate flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" /> Due {formatDueDate(m.due)}
                        </span>
                        {m.achieved_on && <span>Achieved {formatDueDate(m.achieved_on)}</span>}
                        <button
                            onClick={() => window.confirm(`Remove the milestone "${m.name}"?`) && run(`/plans/milestones/${m.id}`, 'DELETE')}
                            disabled={busy}
                            className="inline-flex items-center gap-1 hover:text-graphite"
                        >
                            <Trash2 className="w-3 h-3" /> Remove
                        </button>
                    </p>
                    {mine.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                            {mine.map(t => (
                                <li key={t.id} className={`text-[12px] ${t.status === 'done' ? 'text-coolslate line-through' : 'text-graphite'}`}>
                                    Task: {t.title}
                                </li>
                            ))}
                        </ul>
                    )}
                    {error && <div className="mt-2"><InlineError message={error} /></div>}
                </div>
            </div>
        </li>
    );
}

const input = 'w-full text-sm px-3 py-1.5 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand';

function MilestoneForm({ plan, metrics, currency, onChanged, onClose }: {
    plan: Plan;
    metrics: PlanMetric[];
    currency: string;
    onChanged: () => void;
    onClose: () => void;
}) {
    const { run, busy, error } = usePlanWrite(onChanged);
    const [name, setName] = useState('');
    const [concept, setConcept] = useState('');
    const [target, setTarget] = useState('');
    const [due, setDue] = useState('');
    const metric = metrics.find(m => m.id === concept);

    const save = async () => {
        const body: Record<string, unknown> = { name, due_on: due || null };
        if (concept) Object.assign(body, { concept, target: target === '' ? null : Number(target) });
        if (metric?.unit === 'currency' && currency) body.currency = currency;
        if (await run(`/plans/${plan.id}/milestones`, 'POST', body)) onClose();
    };

    return (
        <div className="mt-3 p-3 rounded-lg bg-offwhite/60 space-y-2">
            <input aria-label="Milestone" placeholder="What will be true, e.g. 100 paying customers" value={name}
                   onChange={e => setName(e.target.value)} className={input} />
            <div className="grid gap-2 sm:grid-cols-3">
                <select aria-label="Measured by" value={concept} onChange={e => setConcept(e.target.value)} className={input}>
                    <option value="">Marked achieved by a person</option>
                    {metrics.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <input aria-label="Target" type="number" placeholder={metric?.unit === 'currency' ? `Target (${currency || 'amount'})` : 'Target'}
                       value={target} onChange={e => setTarget(e.target.value)} disabled={!concept} className={input} />
                <input aria-label="Due" type="date" value={due} max={plan.due_on}
                       onChange={e => setDue(e.target.value)} className={input} />
            </div>
            {error && <InlineError message={error} />}
            <div className="flex items-center gap-3">
                <button onClick={save} disabled={busy || !name.trim()}
                        className="navaris-btn-compact">
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Add milestone
                </button>
                <button onClick={onClose} className="text-[13px] font-medium text-coolslate hover:text-graphite">Cancel</button>
            </div>
        </div>
    );
}

export default function PlanCard({ plan, metrics, currency, tasks, onChanged }: {
    plan: Plan;
    metrics: PlanMetric[];
    currency: string;
    tasks: Task[];
    onChanged: () => void;
}) {
    const { run, busy, error } = usePlanWrite(onChanged);
    const [adding, setAdding] = useState(false);
    const byId = Object.fromEntries(metrics.map(m => [m.id, m]));
    const achieved = plan.milestones.filter(m => m.state === 'achieved').length;

    return (
        <section className="border border-softgrey rounded-xl bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <h4 className="navaris-h3">{plan.name}</h4>
                    <p className="text-[12px] text-coolslate mt-0.5">
                        Due {formatDueDate(plan.due_on)}
                        {plan.milestones.length > 0 && <> · {achieved} of {plan.milestones.length} milestones achieved</>}
                        {plan.created_by && <> · Set up by {plan.created_by}</>}
                    </p>
                    {plan.description && <p className="text-[13px] text-coolslate mt-1">{plan.description}</p>}
                </div>
                <select
                    aria-label="Plan status"
                    value={plan.status}
                    disabled={busy}
                    onChange={e => run(`/plans/${plan.id}`, 'PATCH', { status: e.target.value })}
                    className="text-[12px] px-2 py-1 border border-softgrey rounded-lg bg-white text-graphite"
                >
                    {PLAN_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
            </div>

            {plan.milestones.length > 0 ? (
                <ul className="mt-2 divide-y divide-softgrey/60">
                    {plan.milestones.map(m => (
                        <MilestoneRow key={m.id} milestone={m} metric={m.concept ? byId[m.concept] : undefined}
                                      tasks={tasks} onChanged={onChanged} />
                    ))}
                </ul>
            ) : (
                <p className="mt-3 text-[13px] text-coolslate">No milestones yet.</p>
            )}

            {adding ? (
                <MilestoneForm plan={plan} metrics={metrics} currency={currency} onChanged={onChanged}
                               onClose={() => setAdding(false)} />
            ) : (
                <div className="mt-3 flex flex-wrap items-center gap-4">
                    <button onClick={() => setAdding(true)}
                            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-dark hover:underline">
                        <Plus className="w-3.5 h-3.5" /> Add a milestone
                    </button>
                    <button onClick={() => window.confirm(`Remove the plan "${plan.name}" and its milestones?`) && run(`/plans/${plan.id}`, 'DELETE')}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-coolslate hover:text-graphite">
                        <Trash2 className="w-3.5 h-3.5" /> Remove plan
                    </button>
                </div>
            )}
            {error && <div className="mt-3"><InlineError message={error} /></div>}
        </section>
    );
}
